import { useState } from 'react'
import {
    Alert, Badge, Button, Form, InputGroup, Spinner, Table
} from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_AUTOMATIONS } from '../../gql/queries/campaigns'
import { DELETE_AUTOMATION, TOGGLE_AUTOMATION } from '../../gql/mutations/campaigns'
import { Automation } from '../../types/campaigns'
import { AutomationWizard } from './components/AutomationWizard'
import { AutomationLogModal } from './components/AutomationLogModal'
import { CampaignsNav } from '../campaigns/components/CampaignsNav'

const TRIGGER_LABELS: Record<string, string> = {
    HAPPY_BIRTHDAY: '🎂 Happy Birthday',
    CONDITIONAL_LEAD: '🎯 Conditional (Leads)',
    CONDITIONAL_POLICY: '🛡️ Conditional (Policy)',
}

export const AutomationsPage = () => {
    const [search, setSearch] = useState('')
    const [channelFilter, setChannelFilter] = useState('')
    const [wizardOpen, setWizardOpen] = useState(false)
    const [editingAuto, setEditingAuto] = useState<Automation | null>(null)
    const [logAutoId, setLogAutoId] = useState<Automation | null>(null)

    const { data, loading, error, refetch } = useQuery<{ automations: Automation[] }>(
        GET_AUTOMATIONS,
        {
            variables: { channel: channelFilter || undefined },
            fetchPolicy: 'cache-and-network',
        }
    )

    const [toggleAutomation] = useMutation(TOGGLE_AUTOMATION)
    const [deleteAutomation] = useMutation(DELETE_AUTOMATION)

    const automations = (data?.automations ?? []).filter(
        (a) =>
            !search ||
            a.name.toLowerCase().includes(search.toLowerCase()) ||
            a.description?.toLowerCase().includes(search.toLowerCase())
    )

    const handleToggle = async (automation: Automation) => {
        try {
            await toggleAutomation({ variables: { id: automation.id } })
            toast.success(`Automation ${automation.isActive ? 'disabled' : 'enabled'}`)
            await refetch()
        } catch {
            toast.error('Error changing status')
        }
    }

    const handleDelete = async (automation: Automation) => {
        if (!window.confirm(`Delete "${automation.name}"? This action cannot be undone.`)) return
        try {
            const { data } = await deleteAutomation({ variables: { id: automation.id } })
            if (data?.deleteAutomation?.ok) {
                toast.success('Automation deleted')
                await refetch()
            } else {
                toast.error(data?.deleteAutomation?.errors?.[0] || 'Error deleting automation')
            }
        } catch {
            toast.error('Error deleting automation')
        }
    }

    const handleNew = () => {
        setEditingAuto(null)
        setWizardOpen(true)
    }

    const handleEdit = (a: Automation) => {
        setEditingAuto(a)
        setWizardOpen(true)
    }

    return (
        <div className='automations-page'>
            <CampaignsNav />
            {/* Header */}
            <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
                <div>
                    <h1 className='page-title'>⚡ Automations</h1>
                    <div className='text-muted'>
                        Automated Email and SMS messages based on policy and payment events.
                    </div>
                </div>
                <Button variant='primary' onClick={handleNew}>
                    + New Automation
                </Button>
            </div>

            {/* Filters */}
            <div className='d-flex gap-3 mb-5 flex-wrap'>
                <InputGroup style={{ maxWidth: 320 }}>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                        placeholder='Search automation...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </InputGroup>
                <Form.Select
                    style={{ maxWidth: 180 }}
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                >
                    <option value=''>All channels</option>
                    <option value='EMAIL'>✉️ Email</option>
                    <option value='SMS'>💬 SMS</option>
                </Form.Select>
            </div>

            {/* Error */}
            {error && <Alert variant='danger'>Error loading automations: {error.message}</Alert>}

            {/* Loading */}
            {loading && !data && (
                <div className='text-center py-5'>
                    <Spinner animation='border' />
                </div>
            )}

            {/* Empty */}
            {!loading && automations.length === 0 && (
                <div className='text-center py-5 text-muted'>
                    <div style={{ fontSize: 64 }}>⚡</div>
                    <h5 className='mt-3'>No automations yet</h5>
                    <p>Create your first automation to start sending messages automatically.</p>
                    <Button variant='primary' onClick={handleNew}>
                        + Create first automation
                    </Button>
                </div>
            )}

            {/* Table */}
            {automations.length > 0 && (
                <div className='card'>
                    <div className='card-body p-0'>
                        <Table hover responsive className='mb-0'>
                            <thead className='table-light'>
                                <tr>
                                    <th style={{ width: 40 }}>Status</th>
                                    <th>Name</th>
                                    <th>Scope</th>
                                    <th>Trigger</th>
                                    <th>Channel</th>
                                    <th>Template</th>
                                    <th>Sent</th>
                                    <th>Last sent</th>
                                    <th style={{ width: 150 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {automations.map((a) => (
                                    <tr key={a.id}>
                                        <td className='text-center'>
                                            <Form.Check
                                                type='switch'
                                                id={`auto-switch-${a.id}`}
                                                checked={a.isActive}
                                                onChange={() => handleToggle(a)}
                                                title={a.isActive ? 'Active — click to disable' : 'Inactive — click to enable'}
                                            />
                                        </td>
                                        <td>
                                            <div className='fw-semibold'>{a.name}</div>
                                            {a.description && (
                                                <div className='text-muted small' style={{ maxWidth: 280 }}>
                                                    {a.description}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg={a.scope === 'ORGANIZATION' ? 'secondary' : 'light'} text={a.scope === 'ORGANIZATION' ? 'white' : 'dark'} className="border">
                                                {a.scope === 'ORGANIZATION' ? '🏢 Org' : '👤 Personal'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className='small fw-semibold'>{TRIGGER_LABELS[a.triggerType] ?? a.triggerType}</div>
                                            {(a.triggerType === 'CONDITIONAL_LEAD' || a.triggerType === 'CONDITIONAL_POLICY') && a.triggerConfig?.condition_rule && (
                                                <div className='text-muted' style={{ fontSize: '0.75rem', marginTop: 2 }}>
                                                    {a.triggerConfig.condition_rule}
                                                    {a.triggerConfig.duration_days ? ` (${a.triggerConfig.duration_days}d)` : ''}
                                                    {a.triggerConfig.lead_stage ? ` [${a.triggerConfig.lead_stage}]` : ''}
                                                    {a.triggerConfig.carrier ? ` [${a.triggerConfig.carrier}]` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <Badge
                                                bg={a.channel === 'EMAIL' ? 'primary' : 'info'}
                                                className='text-white'
                                            >
                                                {a.channel === 'EMAIL' ? '✉️ Email' : '💬 SMS'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {a.template
                                                ? <span className='small'>{a.template.name}</span>
                                                : <span className='text-danger small'>No template</span>
                                            }
                                        </td>
                                        <td className='text-center'>
                                            <button
                                                className='btn btn-link btn-sm p-0 text-decoration-none'
                                                onClick={() => setLogAutoId(a)}
                                                title='View history'
                                            >
                                                {a.sentCount ?? 0} /{' '}
                                                <span className='text-muted'>{a.logCount ?? 0}</span>
                                            </button>
                                        </td>
                                        <td>
                                            {a.lastRunAt
                                                ? <span className='small text-muted'>{new Date(a.lastRunAt).toLocaleDateString()}</span>
                                                : <span className='text-muted small'>—</span>
                                            }
                                        </td>
                                        <td>
                                            <div className='d-flex gap-1'>
                                                <Button
                                                    variant='outline-secondary'
                                                    size='sm'
                                                    onClick={() => handleEdit(a)}
                                                    title='Edit'
                                                >
                                                    ✏️
                                                </Button>
                                                <Button
                                                    variant='outline-primary'
                                                    size='sm'
                                                    onClick={() => setLogAutoId(a)}
                                                    title='View history'
                                                >
                                                    📋
                                                </Button>
                                                <Button
                                                    variant='outline-danger'
                                                    size='sm'
                                                    onClick={() => handleDelete(a)}
                                                    title='Delete'
                                                >
                                                    🗑️
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Wizard */}
            <AutomationWizard
                show={wizardOpen}
                onHide={() => setWizardOpen(false)}
                onSaved={() => refetch()}
                automation={editingAuto}
            />

            {/* Log History Modal */}
            <AutomationLogModal
                show={!!logAutoId}
                onHide={() => setLogAutoId(null)}
                automation={logAutoId}
            />
        </div>
    )
}
